import React, { useEffect, useMemo } from 'react';
import { RadioItem } from './RadioItem';
import CustomInput from '../../form/CustomInput';
import styled from 'styled-components';
import { Col, Row } from 'react-bootstrap';
import AMSSelect from '../../AMSSelect';
import AddChoiceIcon from '../../../assets/icons/AddChoiceIcon';
import DeleteChoiceIcon from '../../../assets/icons/DeleteChoiceIcon';
// using dataType string for comparison rather than FieldType
import { FieldLabel } from './FieldSettingsModal';
import { useTheme } from '../../../middleware/ThemeContext';
import { Button } from '../../form/Button';
import WarningIcon from '../../../assets/icons/WarningIcon';
import StyledText from '../../StyledComponents/StyledText';
import { CheckboxItem } from '../../listing/ColumnIdentifier';

interface ChoiceListSettingsProps {
  useGlobalList: boolean;
  setUseGlobalList: (value: boolean) => void;
  options: string[];
  setManualChoices: (choices: string[]) => void;
  choiceFieldType: string;
  setChoiceFieldType: (value: string) => void;
  layout: string;
  setLayout: (value: string) => void;
  choiceSortOrder: 'none' | 'asc' | 'desc';
  setChoiceSortOrder: (value: 'none' | 'asc' | 'desc') => void;
  dataType?: string;
  minChoices: number;
  setMinChoices: (val: number) => void;
  maxChoices: number;
  setMaxChoices: (val: number) => void;
  choiceErrors: number[];
  setChoiceErrors: (val: number[]) => void;
  includeOtherOption: boolean;
  setIncludeOtherOption: (value: boolean) => void;
  defaultSelection?: string;
  setDefaultSelection: (val?: string) => void;
}

export const ChoiceListSettings: React.FC<ChoiceListSettingsProps> = ({
  useGlobalList,
  setUseGlobalList,
  options,
  setManualChoices,
  choiceFieldType,
  setChoiceFieldType,
  layout,
  setLayout,
  choiceSortOrder,
  setChoiceSortOrder,
  dataType,
  minChoices,
  setMinChoices,
  maxChoices,
  setMaxChoices,
  choiceErrors,
  setChoiceErrors,
  includeOtherOption,
  setIncludeOtherOption,
  defaultSelection,
  setDefaultSelection
}) => {
  const { theme } = useTheme();
  const globalChoices = ['A', 'B', 'C', 'D'];
  const choiceFieldTypes = [
    { label: 'Dropdown', value: 'dropdown' },
    { label: 'Radio', value: 'radio' },
  ];
  const layouts = [
    { label: 'List', value: 'list' },
    { label: 'Grid', value: 'grid' },
  ];
  const choiceSortOrders = [
    { label: 'No Sorting', value: 'none' },
    { label: 'Ascending (A → Z)', value: 'asc' },
    { label: 'Descending (Z → A)', value: 'desc' },
  ];
  const optionsCacheKey = useMemo(() => options.join('||'), [options]);
  const defaultOptionChoices = useMemo(() => ([
    { label: 'No default option', value: '' },
    ...options
      .filter((o) => o !== 'Other')
      .map((o) => ({ label: o, value: o })),
  ]), [options]);
  const defaultSelectedOption = useMemo(
    () => defaultOptionChoices.find((opt) => opt.value === (defaultSelection || '')) || null,
    [defaultOptionChoices, defaultSelection]
  );
  const sortOrderSelectedOption = choiceSortOrders.find((opt) => opt.value === choiceSortOrder) || null;
  useEffect(() => {
    if (!useGlobalList && options.length === 0) {
      // Initialize with three blank choices across all supported types; 'Other' is controlled via toggle
      setManualChoices(['', '', '']);
    }
  }
    , [useGlobalList, options, setManualChoices, dataType]);

  // No auto-injection/removal of 'Other' into manual choices; it's driven by includeOtherOption

  const handleAddChoice = (index?: number) => {
    const updated = [...options];
    if (typeof index === 'number') {
      updated.splice(index + 1, 0, '');
    } else {
      updated.push('');
    }
    setManualChoices(updated);
  };

  const handleRemoveChoice = (index: number) => {
    const removedChoice = options[index];
    const updated = options.filter((_, i) => i !== index);
    setManualChoices(updated);
    setChoiceErrors(
      choiceErrors
        .filter((i) => i !== index)
        .map((i) => (i > index ? i - 1 : i))
    );
    if (defaultSelection && defaultSelection === removedChoice) {
      setDefaultSelection(undefined);
    }

  };

  const handleUpdateChoice = (index: number, value: string) => {
    const updated = [...options];
    const prev = updated[index];
    updated[index] = value;
    setManualChoices(updated);
    if (defaultSelection && defaultSelection === prev) {
      if (value && value !== 'Other') setDefaultSelection(value);
      else setDefaultSelection(undefined);
    }
  };

  // Keep defaults valid if options array changes externally
  useEffect(() => {
    if (defaultSelection && !options.includes(defaultSelection)) {
      setDefaultSelection(undefined);
    }
  }, [options, defaultSelection, setDefaultSelection]);

  return (
    <div className="px-3 py-2" style={{ backgroundColor: theme.CorporateTheme.colors.headerBG }}>

      <div className="d-flex gap-4 align-items-center">
        <FieldLabel theme={theme}>
          Add options below
        </FieldLabel>
        <Button
          theme={theme}
          type="button"
          $variant="customize"
          style={{ marginLeft: 'auto' }} // push button to far right
          onClick={async () => {
            try {
              const text = await navigator.clipboard.readText(); // Read clipboard
              if (text) {
                const newChoices = text.split(/\r?\n/).filter(Boolean); // Split by line break
                setManualChoices(newChoices);
                if (defaultSelection && !newChoices.includes(defaultSelection)) {
                  setDefaultSelection(undefined);
                }
              }
            } catch (err) {
              console.error("Failed to read clipboard", err);
            }
          }}
        >
          Paste from Clipboard
        </Button>
      </div>

      {useGlobalList ? (
        <ul className="ps-4">
          {globalChoices.map(choice => (
            <li key={choice}>{choice}</li>
          ))}
        </ul>
      ) : (
        <>
          {options.map((choice, index) => {
            const hasError = choiceErrors.includes(index);

            return (
              <div key={index} className="d-flex gap-2 align-items-center">
                {choiceFieldType === 'radio' && (
                  <RadioItem theme={theme} className="me-2">
                    <input type="radio" name="choiceSource" />
                  </RadioItem>
                )}
                <CustomInput
                  className='my-1'
                  value={choice}
                  onChange={(value: string) => {
                    handleUpdateChoice(index, value);

                    if (choiceErrors.includes(index) && value.trim() !== '') {
                      setChoiceErrors(choiceErrors.filter((i) => i !== index));
                    }
                  }}
                  placeholder={`Choice ${index + 1}`}
                  error={hasError ? 'Please fill this field' : ''}
                />

                <AddChoiceBtn onClick={() => handleAddChoice(index)}>
                  <AddChoiceIcon size={20} />
                </AddChoiceBtn>
                <DeleteChoiceBtn onClick={() => handleRemoveChoice(index)}>
                  <DeleteChoiceIcon size={20} />
                </DeleteChoiceBtn>
              </div>

            )
          })}

          {(dataType === 'SELECT' || dataType === 'RADIO' || dataType === 'MULTIPLE_CHOICES') && (
            <div>
              <CheckboxItem theme={theme}>
                <input
                  type="checkbox"
                  checked={!!includeOtherOption}
                  onChange={(e) => setIncludeOtherOption(e.target.checked)}
                />
                <FieldLabel theme={theme}>Allow ‘Other’ response</FieldLabel>
              </CheckboxItem>
              {includeOtherOption && (
                <div className="mt-1">
                  <StyledText style={{ fontSize: 12, color: theme.CorporateTheme.colors.textSecondary }}>
                    Input will be provided if 'Other' is selected.
                  </StyledText>
                </div>
              )}
            </div>
          )}

          {dataType === 'MULTIPLE_CHOICES' && (
            <div className="mt-2">
              <FieldLabel theme={theme}>Default Option (pre-selected)</FieldLabel>
              <AMSSelect
                staticOptions={defaultOptionChoices}
                value={defaultSelectedOption}
                cacheUniqs={[optionsCacheKey]}
                onChange={(selected) => {
                  const option = Array.isArray(selected) ? selected[0] : selected;
                  const nextValue = option?.value ? String(option.value) : '';
                  if (nextValue) {
                    setDefaultSelection(nextValue);
                  } else {
                    setDefaultSelection(undefined);
                  }
                }}
                placeholder="Select default option"
                isClearable
              />
            </div>
          )}
        </>

      )}

      <FieldLabel theme={theme}>
        Sort Order (When Displayed)
      </FieldLabel>
      <AMSSelect
        staticOptions={choiceSortOrders}
        value={sortOrderSelectedOption}
        onChange={(selected) => {
          const option = Array.isArray(selected) ? selected[0] : selected;
          const nextValue = (option?.value as 'none' | 'asc' | 'desc' | undefined) || 'none';
          setChoiceSortOrder(nextValue);
        }}
        placeholder="Select sort order"
      />
      {/* {(fieldType === 'MULTIPLE_CHOICES') && (
        <Row>
          <Col md={6}>
            <FieldLabel>
              Minimum Selections
            </FieldLabel>
            <CustomInput
              type="number"
              value={minChoices.toString()}
              onChange={(val: string) => {
                const parsed = parseInt(val, 10);
                if (!isNaN(parsed)) setMinChoices(parsed);
              }}
              placeholder="Minimum number"
              min={0}
              max={options.length}
            />
          </Col>
          <Col md={6}>
            <FieldLabel>
              Maximum Selections
            </FieldLabel>
            <CustomInput
              type="number"
              value={maxChoices.toString()}
              onChange={(val: string) => {
                const parsed = parseInt(val, 10);
                if (!isNaN(parsed)) setMaxChoices(parsed);
              }}
              placeholder="Maximum number"
              min={1}
              max={options.length}
            />
          </Col>
        </Row>
      )} */}
    </div>
  );
};

export default ChoiceListSettings;
const BaseChoiceBtn = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  background: white;
  padding: 0;
`;

const AddChoiceBtn = styled(BaseChoiceBtn)`
  border: 2px solid #5CB25C;
`;

const DeleteChoiceBtn = styled(BaseChoiceBtn)`
  border: 2px solid #D33E3E;
`;
